/**
 * Dialog store - Global dialog system with Promise-based API and queue.
 * Replaces DialogContext. Queue ensures only one dialog at a time.
 */

import { create } from 'zustand';
import { useSoundStore } from './soundStore';
import { SOUND_IDS } from '../constants/sounds.constants';

export type DialogConfirmationMode = 'default' | 'deleteAccount' | 'nativeAppPromo'

/** `true` = confirm, `false` = cancel, `'destructive'` = third action (e.g. sign out). */
export type DialogCloseResult = boolean | 'destructive';

export interface DialogOptions {
  title: string;
  message?: string;
  icon?: string;
  type?: 'default' | 'danger' | 'reward';
  confirmText?: string;
  cancelText?: string;
  /** When set, dialog shows a third button (e.g. Sign out) that resolves as `'destructive'`. */
  destructiveText?: string;
  /** When false, the cancel button is hidden (backdrop still resolves false if dismissible). Default true. */
  showCancelButton?: boolean;
  dismissible?: boolean;
  /** When deleteAccount, Dialog renders TextInput; confirm enabled only when phrase matches username. */
  confirmationMode?: DialogConfirmationMode;
  /** Required when confirmationMode is deleteAccount (app username from API). */
  deleteAccountUsername?: string;
}

export interface QueuedDialog extends DialogOptions {
  id: string;
  resolve: (value: DialogCloseResult) => void;
  isAlert: boolean;
}

interface DialogState {
  dialogQueue: QueuedDialog[];
  currentDialog: QueuedDialog | null;
}

interface DialogActions {
  showDialog: (options: DialogOptions) => Promise<DialogCloseResult>;
  showAlert: (options: Omit<DialogOptions, 'cancelText'>) => Promise<boolean>;
  closeDialog: (result: DialogCloseResult) => void;
  handleBackdropPress: () => void;
}

let isShowingRef = false;

export const useDialogStore = create<DialogState & DialogActions>((set, get) => ({
  dialogQueue: [],
  currentDialog: null,

  showDialog: (options: DialogOptions) => {
    return new Promise<DialogCloseResult>((resolve) => {
      const dialog: QueuedDialog = {
        ...options,
        id: `dialog-${Date.now()}-${Math.random()}`,
        resolve,
        isAlert: false,
      };
      const { dialogQueue } = get();
      if (!isShowingRef && dialogQueue.length === 0) {
        isShowingRef = true;
        set({ currentDialog: dialog });
      } else {
        set({ dialogQueue: [...dialogQueue, dialog] });
      }
    });
  },

  showAlert: (options: Omit<DialogOptions, 'cancelText'>) => {
    return new Promise<boolean>((resolve) => {
      const dialog: QueuedDialog = {
        ...options,
        id: `alert-${Date.now()}-${Math.random()}`,
        resolve: (value: DialogCloseResult) => {
          resolve(value === true);
        },
        isAlert: true,
      };
      const { dialogQueue } = get();
      if (!isShowingRef && dialogQueue.length === 0) {
        isShowingRef = true;
        set({ currentDialog: dialog });
      } else {
        set({ dialogQueue: [...dialogQueue, dialog] });
      }
    });
  },

  closeDialog: (result: DialogCloseResult) => {
    const { currentDialog, dialogQueue } = get();
    if (!currentDialog) return;
    if (result === false) {
      useSoundStore.getState().playSound(SOUND_IDS.UI_DISMISS);
    }
    currentDialog.resolve(result);
    isShowingRef = false;
    if (dialogQueue.length > 0) {
      const next = dialogQueue[0];
      set({ currentDialog: next, dialogQueue: dialogQueue.slice(1) });
      isShowingRef = true;
    } else {
      set({ currentDialog: null });
    }
  },

  handleBackdropPress: () => {
    const { currentDialog, closeDialog } = get();
    if (!currentDialog) return;
    const isDismissible = currentDialog.dismissible !== false;
    if (isDismissible) closeDialog(false);
  },
}));
